import { AdPreviewCell } from "@/app/Promotions/Components/AdPreviewCell";
import { AdPreviewDialog } from "@/app/Promotions/Components/AdPreviewDialog";
import {
  ALL_PLATFORMS,
  PlatformIcon,
} from "@/app/Promotions/Components/PlatformIcon";
import { PromotionChannelSubTable } from "@/app/Promotions/Components/PromotionChannelSubTable";
import { syncPromotionChannelMutation } from "@/app/Promotions/GraphQL/promotionChannelMutations";
import {
  PromotionListItem,
  relocationPromotionListQuery,
} from "@/app/Promotions/GraphQL/relocationPromotionListQuery";
import { promotionPlatformLabelMap } from "@/app/Promotions/Utils/promotionMaps";
import type { TableId } from "@/app/Common/Utils/table-id";
import { Link } from "@/components/catalyst/link";
import {
  Currency,
  QueryRelocationPromotionsOrderByColumn,
  RelocationPromotionListQueryVariables,
  SortOrder,
} from "@/gql/graphql";
import { ActionsHook } from "@/lib/Components/Actions/ActionGroupButtons";
import { TimeSinceNow } from "@/lib/Components/Common/TimeSinceNow";
import {
  DataTable,
  DataTableColDef,
  DataTableColumnHeader,
  GetQueryVariablesFn,
  SortingState,
} from "@/lib/Components/DataTable/DataTable";
import { useDialog } from "@/lib/Components/Dialog/Hooks/useDialog";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { useGqlMutation } from "@/lib/GraphQLCodegen/fetcher";
import { EyeIcon } from "@heroicons/react/24/outline";
import { RefreshCwIcon } from "lucide-react";
import type { ReactNode } from "react";
import { toast } from "sonner";
import { useTranslations } from "use-intl";

type PromotionColumnId =
  | "ad"
  | "relocation"
  | "platforms"
  | "budget"
  | "impressions"
  | "clicks"
  | "conversions"
  | "spend"
  | "created_at";

const sortMap: Partial<
  Record<PromotionColumnId, QueryRelocationPromotionsOrderByColumn>
> = {
  budget: QueryRelocationPromotionsOrderByColumn.BudgetAmount,
  impressions: QueryRelocationPromotionsOrderByColumn.Impressions,
  clicks: QueryRelocationPromotionsOrderByColumn.Clicks,
  conversions: QueryRelocationPromotionsOrderByColumn.Conversions,
  spend: QueryRelocationPromotionsOrderByColumn.SpendAmount,
  created_at: QueryRelocationPromotionsOrderByColumn.CreatedAt,
};

// Shared by the table and the ads route loader — both must request the same
// order or the preloaded query key won't match the component's.
export const promotionTableDefaultSorting: SortingState<PromotionColumnId> = [
  { id: "created_at", desc: true },
];

export function getPromotionTableVariables({
  supplierId,
  active,
}: {
  supplierId?: string;
  active?: boolean;
}): GetQueryVariablesFn<
  RelocationPromotionListQueryVariables,
  PromotionColumnId
> {
  return ({ pagination, sorting }) => ({
    first: pagination.pageSize,
    page: pagination.pageIndex,
    supplier_id: supplierId,
    active,
    orderBy: sorting.map((sort) => ({
      column:
        sortMap[sort.id as PromotionColumnId] ??
        QueryRelocationPromotionsOrderByColumn.CreatedAt,
      order: sort.desc ? SortOrder.Desc : SortOrder.Asc,
    })),
  });
}

const numberFormatter = new Intl.NumberFormat("en-US");

function usePromotionActions(): ActionsHook<PromotionListItem> {
  const { open: openPreviewDialog } = useDialog(AdPreviewDialog);
  const { mutateAsync: syncChannel } = useGqlMutation(
    syncPromotionChannelMutation,
  );
  const t = useTranslations("promotion.actions");

  return (promotion) => [
    {
      label: t("preview"),
      items: [
        {
          actions: [
            {
              label: t("preview"),
              Icon: EyeIcon,
              handleAction: () => {
                openPreviewDialog({ creative: promotion.creative ?? null });
              },
            },
            {
              label: t("resyncAll"),
              Icon: RefreshCwIcon,
              handleAction: async () => {
                await Promise.all(
                  promotion.channels.map((channel) =>
                    syncChannel({ id: channel.id }),
                  ),
                );

                toast.success(t("resyncAllSuccess"));
              },
            },
          ],
        },
      ],
    },
  ];
}

export function PromotionTable({
  id,
  supplierId,
  active,
  rightButtons,
}: {
  id: TableId;
  supplierId?: string;
  active?: boolean;
  rightButtons?: ReactNode;
}) {
  const t = useTranslations("promotion.table");
  const getActions = usePromotionActions();

  const columns: DataTableColDef<
    PromotionListItem,
    unknown,
    PromotionColumnId
  >[] = [
    {
      id: "ad",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("ad")} />
      ),
      accessorFn: (row) => row.creative?.title,
      cell: ({ row }) => <AdPreviewCell creative={row.original.creative} />,
    },
    {
      id: "relocation",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("relocation")} />
      ),
      accessorFn: (row) => row.relocation.name,
      cell: ({ row }) => (
        <div>
          <Link
            from="/$tenant/$orgId/supplier/{-$supplierId}"
            to="/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId"
            params={(prev: Record<string, unknown>) => ({
              ...prev,
              relocationId: row.original.relocation.id,
            })}
            className="whitespace-nowrap font-medium"
          >
            {row.original.relocation.name}
          </Link>

          <p className="whitespace-nowrap text-xs text-gray-500">
            {row.original.relocation.reference} ·{" "}
            {row.original.relocation.supplier.name}
          </p>
        </div>
      ),
    },
    {
      id: "platforms",
      enableSorting: false,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("platforms")} />
      ),
      accessorFn: (row) => row.channels.length,
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {ALL_PLATFORMS.map((platform) => {
            const isRunning = row.original.channels.some(
              (channel) => channel.platform === platform,
            );

            return (
              <PlatformIcon
                key={platform}
                platform={platform}
                enabled={isRunning}
                title={promotionPlatformLabelMap[platform]}
              />
            );
          })}
        </div>
      ),
    },
    {
      id: "budget",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("budget")} />
      ),
      accessorFn: (row) => row.budget_amount,
      cell: ({ row }) =>
        formatCurrency(row.original.budget_amount, Currency.Usd, {
          showZero: true,
        }),
    },
    {
      id: "impressions",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("impressions")} />
      ),
      accessorFn: (row) => row.impressions,
      cell: ({ row }) => numberFormatter.format(row.original.impressions),
    },
    {
      id: "clicks",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("clicks")} />
      ),
      accessorFn: (row) => row.clicks,
      cell: ({ row }) => numberFormatter.format(row.original.clicks),
    },
    {
      id: "conversions",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("conversions")} />
      ),
      accessorFn: (row) => row.conversions,
      cell: ({ row }) => numberFormatter.format(row.original.conversions),
    },
    {
      id: "spend",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("spend")} />
      ),
      accessorFn: (row) => row.spend_amount,
      cell: ({ row }) =>
        formatCurrency(row.original.spend_amount, Currency.Usd, {
          showZero: true,
        }),
    },
    {
      id: "created_at",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("created")} />
      ),
      accessorFn: (row) => row.created_at,
      cell: ({ row }) => <TimeSinceNow time={row.original.created_at} />,
    },
  ];

  return (
    <DataTable
      id={id}
      columns={columns}
      document={relocationPromotionListQuery}
      accessor={(data) => data.relocationPromotions}
      initialSorting={promotionTableDefaultSorting}
      getActions={getActions}
      rightButtons={rightButtons}
      searchable={false}
      renderSubRow={(promotion) =>
        promotion.channels.length > 0 ? (
          <PromotionChannelSubTable channels={promotion.channels} />
        ) : null
      }
      getQueryVariables={getPromotionTableVariables({ supplierId, active })}
    />
  );
}
